import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { redoMarkSvg } from './brandMark';

export const THEME_STORAGE_KEY = 'redo:theme';

export const THEME_OPTIONS = [
  { id: 'gambit', name: 'Gambit', background: '#070709', surface: '#12121a', elevated: '#1b1b26', accent: '#8b5cf6', brightAccent: '#A78BFA', text: '#f8fafc' },
  { id: 'matrix', name: 'Matrix', background: '#000', surface: '#050a05', elevated: '#0a1f0a', accent: '#0c6', brightAccent: '#32E584', text: '#0f8' },
  { id: 'linear', name: 'Linear', background: '#08090a', surface: '#131418', elevated: '#1c1d22', accent: '#5e6ad2', brightAccent: '#7C8AED', text: '#f7f8f8' },
  { id: 'slate', name: 'Tournament Slate', background: '#08090c', surface: '#111419', elevated: '#1b2027', accent: '#d8b46a', brightAccent: '#E7C77C', text: '#f6efe3' },
  { id: 'nebula', name: 'Nebula Command', background: '#061015', surface: '#0d1a21', elevated: '#142632', accent: '#53d6cf', brightAccent: '#7CE7E1', text: '#edf8fb' },
  { id: 'parlor', name: 'Club Parlor', background: '#11130f', surface: '#192119', elevated: '#253226', accent: '#e0bd72', brightAccent: '#F0D38E', text: '#fff8ea' },
  { id: 'blueprint', name: 'Blueprint', background: '#071018', surface: '#0c1721', elevated: '#122333', accent: '#6aa8ff', brightAccent: '#8FC1FF', text: '#f1f7fb' },
  { id: 'classicGreen', name: 'Classic Green', background: '#0A0C0A', surface: '#141813', elevated: '#1B211A', accent: '#6BBF6B', brightAccent: '#8DD28D', text: '#F5F7FA' },
] as const;

export type Theme = typeof THEME_OPTIONS[number]['id'];

const DEFAULT_THEME: Theme = 'gambit';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readStoredTheme(): Theme {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (THEME_OPTIONS.some(option => option.id === raw)) return raw as Theme;
  } catch {
    // localStorage unavailable (private mode, SSR) — fall through to default
  }
  return DEFAULT_THEME;
}

function applyThemeAttribute(theme: Theme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
    const palette = THEME_OPTIONS.find(option => option.id === theme)!;
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icon) {
      icon.type = 'image/svg+xml';
      icon.href = `data:image/svg+xml,${encodeURIComponent(redoMarkSvg(palette.accent))}`;
    }
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', palette.background);
  }
}

// Run at module load so the html attribute is set before React mounts, avoiding
// a one-frame flash when a non-default theme is persisted. Safe to call multiple
// times.
export function initializeTheme(): Theme {
  const theme = readStoredTheme();
  applyThemeAttribute(theme);
  return theme;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  useEffect(() => {
    applyThemeAttribute(theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // ignore — persistence is best-effort
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside a ThemeProvider');
  }
  return ctx;
}
